import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api, API } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      // Attribute a pending referral once, right after a successful signup/login.
      try {
        const ref = localStorage.getItem("facio_ref");
        if (ref && !res.data?.referred_by) {
          await api.post("/referral/attribute", { code: ref });
        }
        if (ref) localStorage.removeItem("facio_ref");
      } catch (_) {}
      // Remember the last successfully synced balance for offline display only
      // (never used to authorize an export — the server always enforces credits).
      try {
        localStorage.setItem("facio_synced_at", String(Date.now()));
        const bal = res.data?.active_workspace ? res.data?.effective_credits : res.data?.credits;
        if (bal != null) localStorage.setItem("facio_last_balance", String(bal));
      } catch (_) {}
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed (clearing session anyway):", e);
    }
    setUser(null);
    window.location.href = "/";
  }, []);

  const switchWorkspace = useCallback(async (workspaceId) => {
    const res = await api.post("/workspaces/switch", { workspace_id: workspaceId });
    setUser(res.data);
    return res.data;
  }, []);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Server-side Google OAuth (our own client): backend redirects to Google, then back to /studio.
    window.location.href = `${API}/oauth/google/login`;
  }, []);

  const setCredits = useCallback((credits) => setUser((u) => (u ? { ...u, credits } : u)), []);
  const refreshUser = checkAuth;

  const value = useMemo(
    () => ({ user, setUser, loading, login, logout, refreshUser, setCredits, switchWorkspace }),
    [user, loading, login, logout, refreshUser, setCredits, switchWorkspace],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
